import { Ref, watchEffect } from 'vue';
import {
  FieldHtmlNameUtil,
  FieldHtmlNameType,
  OptionListUtil,
  HekateDisplayField,
  HekateDisplayForm,
  HekateFormDefinition,
  ErrorTagUtil,
  HekateEventBusCallbackParams,
} from 'www-shared';

import ExternalUtil from '../../../ExternalUtil';
import hekateBus from '../../../../bus';
import ErrorUtil from '../../../util/ErrorUtil';
import ProgressiveProfileDefinition from './ProgressiveProfileDefinition';
import { ProgressRule } from './ProgressRule';
import { FormSubmitResolution } from '../../../../model/HekateFormSubmission';

export default abstract class BaseProgressiveProfileDefinition implements ProgressiveProfileDefinition {
  /** Fields values that should be stored and pre-filled */
  private readonly basePreFilledFields: readonly FieldHtmlNameType[] = [
    FieldHtmlNameUtil.firstName,
    FieldHtmlNameUtil.lastName,
    FieldHtmlNameUtil.email,
    FieldHtmlNameUtil.pNumber,
    FieldHtmlNameUtil.phoneNumber,
    FieldHtmlNameUtil.companyName,
    FieldHtmlNameUtil.jobTitle,
    FieldHtmlNameUtil.privacyConsentAccepted,
    FieldHtmlNameUtil.maxCountry,
    FieldHtmlNameUtil.maxState,
    FieldHtmlNameUtil.maxCity,
  ];

  abstract getProgressRules(): readonly ProgressRule[];

  abstract getBranchField(): FieldHtmlNameType;

  abstract getAlwaysShownFields(): readonly FieldHtmlNameType[];

  // eslint-disable-next-line class-methods-use-this
  public onSuccess({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formDefinition,
    redirectUrl,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formSubmitResolution,
  }:{
    formDefinition: Ref<HekateDisplayForm>,
    redirectUrl: string | null,
    formSubmitResolution?: Promise<FormSubmitResolution | null>,
  }): void {
    if (redirectUrl) {
      // The timeout is to allow the `dataLayer.push` to fire.
      setTimeout(() => {
        document.location.href = redirectUrl;
      }, 850);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public onEloquaDown({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formDefinition,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    redirectUrl,
  }: {
    formDefinition: Ref<HekateFormDefinition>,
    redirectUrl: string | null,
  }): void {
    ErrorUtil.safePostMessageToSentry({
      tag: ErrorTagUtil.eloquaDown,
      msg: `${formDefinition.value.name} (${formDefinition.value.id})`,
    });
  }

  public getPreFilledFields(): readonly FieldHtmlNameType[] {
    return this.basePreFilledFields;
  }

  /** Make sure all fields that are used in the definition are in `fields` */
  public isAllFormRequiredFieldsPresent(fields: HekateDisplayField[]): string[] {
    const fieldsToCheck: { [key: string]: boolean; } = {};
    fieldsToCheck[this.getBranchField()] = false;
    this.getAlwaysShownFields().forEach((field) => {
      fieldsToCheck[field] = false;
    });
    this.getProgressRules().forEach((rule) => {
      rule.fieldToShow.forEach((field) => {
        fieldsToCheck[field] = false;
      });
    });

    fields.forEach((field) => {
      fieldsToCheck[field.htmlName] = true;
    });

    const missing = Object.entries(fieldsToCheck)
      .filter(([, vv]) => !vv)
      .map(([kk]) => kk);

    return missing;
  }

  // eslint-disable-next-line class-methods-use-this
  public realtimeBranchModifications({
    formDefinition,
  }: {
    formDefinition: Ref<HekateDisplayForm>,
  }): void {
    hekateBus.addEventListener('hide-show-form', (evt: HekateEventBusCallbackParams<{ formId: string, action: 'hide' | 'show' }>) => {
      if (evt.data.formId !== formDefinition.value.id) {
        return;
      }
      if (evt.data.action === 'hide') {
        formDefinition.value.isHidden = true;
      }
      if (evt.data.action === 'show') {
        formDefinition.value.isHidden = false;
      }
    });

    BaseProgressiveProfileDefinition.watchCountries({ formDefinition });
  }

  private static watchCountries({
    formDefinition,
  }: {
    formDefinition: Ref<HekateDisplayForm>,
  }) {
    watchEffect(() => {
      if (formDefinition.value === null) {
        return;
      }
      const foundCountry = ExternalUtil.findDisplayFieldOrNull({ formDefinition, fieldName: FieldHtmlNameUtil.maxCountry });
      const foundState = formDefinition.value.displayFields.find((field: HekateDisplayField) => field.htmlName === FieldHtmlNameUtil.maxState);
      if (!foundState || !foundCountry) {
        return;
      }

      if (foundCountry.componentName === 'hidden-field' || foundState.componentName === 'hidden-field') {
        return;
      }

      if (foundCountry.val === 'United States') {
        foundState.componentName = 'single-select-field';
        foundState.optionList.elements.splice(0, foundState.optionList.elements.length);
        OptionListUtil.usStates.forEach((state) => {
          foundState.optionList.elements.push(state);
        });
      } else if (foundCountry.val === 'Canada') {
        foundState.componentName = 'single-select-field';
        foundState.optionList.elements.splice(0, foundState.optionList.elements.length);
        OptionListUtil.canadaProvinces.forEach((province) => {
          foundState.optionList.elements.push(province);
        });
      } else {
        foundState.optionList.elements.splice(0, foundState.optionList.elements.length);
        foundState.componentName = 'text-input-field';
      }
      foundState.notifyChange();
    });
  }
}
